<template>
	<div>
		<page-authorization-container :page="page">
			<h2 class="mb-4">Output Maps</h2>

			<div v-if="$parent.scenario.status.isRunning">
				<page-running />
			</div>
			<div v-else>
				<b-row>
					<b-col md="6">
						<b-form @submit.prevent="save">
							<b-form-group :invalid-feedback="requiredFeedback($v.selection.outputType)">
								<b-form-select v-model="selection.outputType" :options="options.outputTypes.types" :state="getValidState($v.selection.outputType)" @change="selection.parameter = null">
									<template v-slot:first>
										<option :value="null" disabled>-- Select an output type --</option>
									</template>
								</b-form-select>
							</b-form-group>
							<b-form-group :invalid-feedback="requiredFeedback($v.selection.statistic)">
								<b-form-select v-model="selection.statistic" :options="options.statistics" :state="getValidState($v.selection.statistic)">
									<template v-slot:first>
										<option :value="null" disabled>-- Select a statistic --</option>
									</template>
								</b-form-select>
							</b-form-group>
							<b-form-group v-if="!isNullOrEmpty(selection.outputType)" :invalid-feedback="requiredFeedback($v.selection.parameter)">
								<b-form-select v-model="selection.parameter" :state="getValidState($v.selection.parameter)">
									<template v-slot:first>
										<option :value="null" disabled>-- Select a parameter --</option>
									</template>
									<option v-for="(o, i) in options.outputTypes.parameters[selection.outputType]" :key="i" :value="o.variable">
										{{o.variable}} - {{o.description}}
									</option>
								</b-form-select>
							</b-form-group>
							<b-form-group v-if="!isNullOrEmpty(selection.outputType)" :invalid-feedback="requiredFeedback($v.selection.units)">
								<b-form-select v-model="selection.units" :options="options.outputTypes.units" :state="getValidState($v.selection.units)"></b-form-select>
								<small class="text-muted">Set the default units displayed from your <router-link to="/account">account settings</router-link></small>
							</b-form-group>

							<div v-if="options.scenarios.length > 0" class="my-3">
								<div class="checklist-header">
									Select up to {{page.maxToCompare}} other scenarios for output comparison (optional)
								</div>
								<div class="checklist">
									<b-form-checkbox-group v-model="selection.compareScenarios" stacked>
										<div class="item" v-for="(o, i) in options.scenarios" :key="i">
											<b-form-checkbox :value="o.value">{{o.text}}</b-form-checkbox>
										</div>
									</b-form-checkbox-group>
								</div>
							</div>

							<b-alert :show="selection.compareScenarios.length > page.maxToCompare" variant="warning">
								You may only compare to up to {{page.maxToCompare}} scenarios at a time. Please modify your selection.
							</b-alert>

							<error-list :errors="page.saveErrors"></error-list>

							<div class="mt-4 mb-3">
								<save-button :saving="page.saving" class="mr-2" text="Get Map" :disabled="isNullOrEmpty(selection.parameter) || selection.compareScenarios.length > page.maxToCompare" />
								<b-button v-if="maps.scenarioMaps.length > 0" type="button" variant="secondary" @click="page.editColors.show = true">Change Colors</b-button>
							</div>
						</b-form>
					</b-col>
					<b-col md>
						<project-map :project-map="options.map" height="300px"></project-map>
					</b-col>
				</b-row>

				<b-row class="my-3">
					<b-col md="6" v-for="(map, i) in maps.scenarioMaps" :key="i">
						<div class="my-3">
							<highcharts :constructor-type="'mapChart'" :options="map" class="map"></highcharts>
						</div>
					</b-col>
				</b-row>

				<b-modal v-model="page.editColors.show" size="lg" title="Change map colors">
					<b-row>
						<b-col md>
							<b-form-group label="Minimum value color">
								<b-form-input type="color" v-model="mapColors.min"></b-form-input>
							</b-form-group>
						</b-col>
						<b-col md>
							<b-form-group label="Median value color">
								<b-form-input type="color" v-model="mapColors.mid"></b-form-input>
							</b-form-group>
						</b-col>
					</b-row>

					<b-row>
						<b-col md>
							<b-form-group label="Maximum value color">
								<b-form-input type="color" v-model="mapColors.max"></b-form-input>
							</b-form-group>
						</b-col>
						<b-col md>
							<b-form-group label="Stream color">
								<b-form-input type="color" v-model="maps.riverSeries.nullColor"></b-form-input>
							</b-form-group>
						</b-col>
					</b-row>

					<b-row>
						<b-col md>
							<b-button @click="reverseColors" variant="light" typeof="button">Reverse min/max colors</b-button>
							<div class="mt-2">
								<b-form-checkbox v-model="mapColors.showLabels" switch>Show labels</b-form-checkbox>
							</div>
						</b-col>
						<b-col md>
							<b-form-group label="Color axis scale">
								<b-form-select v-model="mapColors.scale">
									<option>linear</option>
									<option>logarithmic</option>
								</b-form-select>
							</b-form-group>
						</b-col>
					</b-row>

					<div slot="modal-footer">
						<save-button type="button" :saving="false" @click.native="drawMaps(); page.editColors.show = false" text="Ok" />
						<b-button type="button" variant="secondary" @click="page.editColors.show = false" class="ml-1">Cancel</b-button>
					</div>
				</b-modal>
			</div>
		</page-authorization-container>
	</div>
</template>

<script>
	import { required } from 'vuelidate/lib/validators';
	import ProjectMap from '@/components/ProjectMap';

	export default {
		name: 'OutputMaps',
		components: {
			ProjectMap
		},
		data() {
			return {
				scenarioID: this.$route.params.scenarioID,
				projectID: this.$route.params.id,
				page: {
					errors: [],
					loading: false,
					showLogin: false,
					saveErrors: [],
					saving: false,
					maxToCompare: 3,
					editColors: {
						show: false
					}
				},
				options: {
					outputTypes: {
						types: [],
						parameters: [],
						units: []
					},
					scenarios: [],
					statistics: [],
					map: {}
				},
				geojson: {
					subbasins: {},
					streams: {}
				},
				mapColors: {
					min: '#e6ebf5',
					mid: '#266eff',
					max: '#003399',
					hover: '#70dbe5',
					scale: 'linear',
					showLabels: false
				},
				selection: {
					statistic: null,
					outputType: null,
					parameter: null,
					compareScenarios: [],
					units: 0
				},
				maps: {
					riverSeries: {
						name: 'Streams',
						type: 'mapline',
						mapData: null,
						nullColor: '#7cb5ec'
					},
					scenarioMaps: []
				},
				responseData: {
					scenarioMaps: []
				}
			}
		},
		validations: {
			selection: {
				statistic: { required },
				outputType: { required },
				parameter: { required },
				units: { required }
			}
		},
		async created() {
			await this.get();
		},
		methods: {
			async get() {
				this.page.errors = [];
				this.page.loading = true;

				try {
					const response = await this.$http.get(`scenarios/output/mapoptions/${this.scenarioID}`, this.getTokenHeader());
					this.log(response.data);
					this.options = response.data;
					this.selection.units = this.options.outputTypes.defaultUnits;

					const subsResponse = await this.$httpRoot.get(this.options.map.geoJsonUrls.subbasins);
					const streamsResponse = await this.$httpRoot.get(this.options.map.geoJsonUrls.streams);
					this.geojson.subbasins = subsResponse.data;
					this.geojson.streams = streamsResponse.data;
				} catch (error) {
					if (this.isApiUnauthorized(error)) this.page.showLogin = true;
					else this.page.errors = this.logError(error);
				}

				this.page.loading = false;
			},
			async save() {
				this.page.saveErrors = [];
				this.page.saving = true;
				this.$v.$touch();

				if (this.$v.$invalid) {
					this.page.saveErrors.push('Please complete the form.');
				} else {
					this.log(this.selection);

					try {
						const response = await this.$http.post(`scenarios/output/map/${this.scenarioID}`, this.selection, this.getTokenHeader());
						this.log(response.data);
						this.responseData = response.data;
						this.drawMaps(true);

						this.$v.$reset();
					} catch (error) {
						if (this.isApiUnauthorized(error)) this.page.showLogin = true;
						else this.page.saveErrors = this.logError(error);
					}
				}

				this.page.saving = false;
			},
			reverseColors() {
				var max = this.mapColors.max;
				this.mapColors.max = this.mapColors.min;
				this.mapColors.min = max;
			},
			getColorAxis(map, initialize = false) {
				if (initialize && (map.maxValue - map.minValue) >= 10000 && map.minValue > 0) {
					this.mapColors.scale = 'logarithmic';
				}

				return {
					type: !map.allowLogAxis ? 'linear' : this.mapColors.scale,
					allowNegativeLog: true,
					minColor: this.mapColors.min,
					maxColor: this.mapColors.max,
					stops: [
						[0, this.mapColors.min],
						[0.5, this.mapColors.mid],
						[1, this.mapColors.max]
					]
				}
			},
			drawMaps(initialize = false) {
				this.maps.riverSeries.mapData = this.geojson.streams;

				this.maps.scenarioMaps = [];
				for (var i = 0; i < this.responseData.scenarioMaps.length; i++) {
					var map = this.responseData.scenarioMaps[i];
					this.maps.scenarioMaps.push(
						{
							title: { text: map.mapTitle },
							legend: {
								title: {
									text: map.legendTitle
								}
							},
							colorAxis: this.getColorAxis(map, initialize),
							xAxis: {
								min: map.xAxisMin,
								max: map.xAxisMax
							},

							yAxis: {
								min: map.yAxisMin,
								max: map.yAxisMax
							},
							mapNavigation: {
								enabled: true
							},
							series: [{
								data: map.mapData,
								mapData: this.geojson.subbasins,
								joinBy: ['name', 'code'],
								name: map.mapDataValuesName,
								states: {
									hover: {
										color: this.mapColors.hover
									}
								},
								dataLabels: {
									enabled: this.mapColors.showLabels,
									format: '{point.properties.name}'
								},
								tooltip: {
									pointFormat: '{point.code}: {point.value:.2f} ' + map.units
								}
							}, this.maps.riverSeries]
						}
					)
				}
			}
		}
	}
</script>
